import { ArrowRightOutlined } from '@ant-design/icons';
import { useRef } from 'react';
import styled from 'styled-components';

import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Transition from '@/components/Common/Transition';
import Heading from '@/components/Typography/Heading';
import { Paragraph } from '@/components/Typography/Typo';
import {
  type HeySynclyChat,
  HeySynclyMessageDisplay,
} from '@/entities/hey-syncly';
import type { DashboardCardInfoT } from '@/features/Dashboard/types';
import useColor from '@/hooks/domain/useColor';
import { useMessage } from '@/messages';
import useHeySynclyStore from '@/stores/HeySynclyStore';
import { customScrollbar } from '@/styles/style';
import { GPT_REQUEST_TYPE } from '@/types/gptConst';

type Props = {
  onGptActionClick: (action: GPT_REQUEST_TYPE) => void;
  dashboardCardInfo?: DashboardCardInfoT;
  chart?: ChartGptPayload;
  chat?: HeySynclyChat;
};

const PromptArea = (props: Props) => {
  const color = useColor();
  const answer = useHeySynclyStore((state) => state.tempAnswer);
  const containerRef = useRef<HTMLDivElement>(null);
  const chat = props.chat;
  const m = useMessage();

  return (
    <Style ref={containerRef}>
      <Transition
        status={!!chat}
        style={{ paddingBottom: 32 }}
        fallback={
          <VFlex>
            <Flex a="center" g={6}>
              <AIIcon className="text-2xl" />
              <Heading type="h2">Ask any questions</Heading>
            </Flex>
            <VFlex padding="8px 0px 0px 30px" g={40}>
              <Paragraph size={1}>
                What can I help you? Feel free to ask a follow-up question and
                here are some popular questions you can use.
              </Paragraph>
              <VFlex g={8} a="flex-start">
                <Button
                  sizeType="m"
                  variant="outlined"
                  rightIcon={<ArrowRightOutlined />}
                  rounded
                  style={{ backgroundColor: color.gray100 }}
                  onClick={() =>
                    props.onGptActionClick(GPT_REQUEST_TYPE.SUMMARY)
                  }
                >
                  {m.heySyncly('GPT_TEMPLATE_SUMMARY') as string}
                </Button>
                <Button
                  sizeType="m"
                  variant="outlined"
                  rightIcon={<ArrowRightOutlined />}
                  rounded
                  style={{ backgroundColor: color.gray100 }}
                  onClick={() =>
                    props.onGptActionClick(GPT_REQUEST_TYPE.ACTION)
                  }
                >
                  {m.heySyncly('GPT_TEMPLATE_ACTION') as string}
                </Button>
              </VFlex>
            </VFlex>
          </VFlex>
        }
      >
        {chat && (
          <div className="relative w-full">
            <HeySynclyMessageDisplay
              chat_id={chat.alias}
              messages={chat?.messages || []}
              status={chat?.status}
              answer={answer}
              onStudioItemClick={() => {}}
              container={containerRef.current}
              hideFeedbackCount
              hideReason
            />
          </div>
        )}
      </Transition>
    </Style>
  );
};

const Style = styled(VFlex)`
  flex: 1;
  position: relative;
  overflow: auto;
  ${customScrollbar};
`;

export default PromptArea;
